import Layout from "@/components/Layout";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import {
  fetchDashboard,
  fetchInventoryTrend,
  type InventoryTrendRange,
} from "@/services/dashboardService";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Car, Users, Truck, UserCheck, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { format, isValid, parseISO, startOfMonth } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const statIcons: Record<string, LucideIcon> = {
  products: Car,
  customers: Users,
  suppliers: Truck,
  employees: UserCheck,
};

const statRoutes: Record<string, string> = {
  products: "/products?inventory_status=in_stock",
  customers: "/customers",
  suppliers: "/suppliers",
  employees: "/employees",
};

const statCardThemes = [
  { border: "border-sky-200", iconBg: "bg-sky-50 dark:bg-sky-950/40", iconText: "text-sky-500 dark:text-sky-400" },
  { border: "border-emerald-200", iconBg: "bg-emerald-50 dark:bg-emerald-950/40", iconText: "text-emerald-500 dark:text-emerald-400" },
  { border: "border-amber-200", iconBg: "bg-amber-50 dark:bg-amber-950/40", iconText: "text-amber-500 dark:text-amber-400" },
  { border: "border-violet-200", iconBg: "bg-violet-50 dark:bg-violet-950/40", iconText: "text-violet-500 dark:text-violet-400" },
];

const TREND_RANGE_OPTIONS: { id: InventoryTrendRange; label: string }[] = [
  { id: "month", label: "This month" },
  { id: "last_7_days", label: "Last 7 Days" },
  { id: "custom", label: "Custom Date Range" },
];

const formatInt = (n: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);

const formatChange = (n: number) => `${n >= 0 ? "+" : ""}${n}`;

function parseIsoDate(iso: string): Date | undefined {
  if (!iso.trim()) return undefined;
  const d = parseISO(iso.slice(0, 10));
  return isValid(d) ? d : undefined;
}

function buildChartYAxis(maxValue: number): { yMax: number; yTicks: number[] } {
  const yMax = Math.max(4, Math.ceil(maxValue * 1.15));
  const step = yMax <= 8 ? 1 : Math.ceil(yMax / 5);
  const yTicks: number[] = [];
  for (let t = 0; t <= yMax; t += step) {
    yTicks.push(t);
  }
  if (yTicks[yTicks.length - 1] !== yMax) {
    yTicks.push(yMax);
  }
  return { yMax, yTicks };
}

const Dashboard = () => {
  const todayLine = format(new Date(), "EEEE, MMMM d, yyyy");
  const now = new Date();

  const [trendRange, setTrendRange] = useState<InventoryTrendRange>("month");
  const [customDraftFrom, setCustomDraftFrom] = useState(() => format(startOfMonth(now), "yyyy-MM-dd"));
  const [customDraftTo, setCustomDraftTo] = useState(() => format(now, "yyyy-MM-dd"));
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ from: string; to: string }>(() => ({
    from: format(startOfMonth(now), "yyyy-MM-dd"),
    to: format(now, "yyyy-MM-dd"),
  }));
  const [customRangeError, setCustomRangeError] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: fetchDashboard,
  });

  const trendQueryParams = useMemo(() => {
    if (trendRange === "custom") {
      return {
        range: "custom" as const,
        from: appliedCustomRange.from,
        to: appliedCustomRange.to,
      };
    }
    return { range: trendRange };
  }, [trendRange, appliedCustomRange]);

  const {
    data: trendData,
    isLoading: trendLoading,
    isError: trendIsError,
    error: trendError,
  } = useQuery({
    queryKey: ["dashboard-inventory-trend", trendQueryParams],
    queryFn: () => fetchInventoryTrend(trendQueryParams),
  });

  const trendChartData = trendData?.trend ?? [];
  const { yMax, yTicks } = useMemo(() => {
    const peak = trendChartData.reduce((m, d) => Math.max(m, d.primary, d.secondary), 0);
    return buildChartYAxis(peak);
  }, [trendChartData]);

  const xAxisInterval = useMemo(() => {
    if (trendChartData.length <= 8) return 0;
    return Math.max(0, Math.floor(trendChartData.length / 8) - 1);
  }, [trendChartData.length]);

  const stats = data?.stats ?? [];

  const selectTrendRange = (next: InventoryTrendRange) => {
    setCustomRangeError("");
    setTrendRange(next);
    if (next === "custom") {
      setCustomDraftFrom(appliedCustomRange.from);
      setCustomDraftTo(appliedCustomRange.to);
    }
  };

  const applyCustomRange = () => {
    const from = customDraftFrom.trim();
    const to = customDraftTo.trim();
    if (!from || !to) {
      setCustomRangeError("Select both start and end dates.");
      return;
    }
    const fromDate = parseIsoDate(from);
    const toDate = parseIsoDate(to);
    if (!fromDate || !toDate) {
      setCustomRangeError("Invalid date selected.");
      return;
    }
    if (fromDate > toDate) {
      setCustomRangeError("Start date must be on or before end date.");
      return;
    }
    setCustomRangeError("");
    setAppliedCustomRange({ from, to });
    setTrendRange("custom");
  };

  const customFromDate = parseIsoDate(customDraftFrom);
  const customToDate = parseIsoDate(customDraftTo);
  const canApplyCustomRange =
    Boolean(customFromDate && customToDate) &&
    customFromDate! <= customToDate! &&
    (customDraftFrom !== appliedCustomRange.from || customDraftTo !== appliedCustomRange.to || trendRange !== "custom");

  return (
    <Layout title="Dashboard">
      <div className="w-full space-y-8">
        <header>
          <p className="text-sm text-muted-foreground">
            Overview of your inventory for {todayLine}
          </p>
        </header>

        {isError ? (
          <p className="text-sm text-destructive" role="alert">
            {error instanceof Error ? error.message : "Could not load dashboard data."}
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`sk-${i}`}
                  className="rounded-lg border border-border/80 bg-muted/40 p-4 h-[120px] animate-pulse"
                />
              ))
            : stats.map((stat, i) => {
                const theme = statCardThemes[i % statCardThemes.length];
                const Icon = statIcons[stat.key] ?? Car;
                const href = statRoutes[stat.key];
                const card = (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className={`rounded-lg bg-card text-card-foreground shadow-sm border transition-shadow ${theme.border} ${href ? "hover:shadow-md" : ""}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {stat.label}
                          </p>
                          <p className="text-3xl font-bold tracking-tight tabular-nums">
                            {formatInt(stat.value)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className={stat.change_up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>
                              {formatChange(stat.change)}
                            </span>{" "}
                            vs prior 7 days
                          </p>
                        </div>
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${theme.iconBg}`}
                        >
                          <Icon className={`h-5 w-5 ${theme.iconText}`} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );

                return href ? (
                  <Link key={stat.key} to={href} className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    {card}
                  </Link>
                ) : (
                  <div key={stat.key}>{card}</div>
                );
              })}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-border/80 bg-card p-6 shadow-sm"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">Inventory Trend</h2>
              <p className="mt-1 text-xs italic text-muted-foreground">
                Products added vs. sales recorded over the selected date range.
              </p>
            </div>

            <div className="flex flex-col items-stretch gap-2 lg:items-end shrink-0">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background self-start lg:self-end">
                {TREND_RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => selectTrendRange(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors whitespace-nowrap",
                      trendRange === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {trendRange === "custom" ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-end">
                  <div className="w-full sm:w-[11.5rem] space-y-1">
                    <p className="text-[11px] font-medium text-muted-foreground">From</p>
                    <DatePartsPicker
                      value={customDraftFrom}
                      onChange={(iso) => {
                        setCustomRangeError("");
                        setCustomDraftFrom(iso);
                      }}
                      fromYear={2020}
                      toYear={now.getFullYear()}
                      maxDate={customToDate ?? now}
                      placeholder="Start date"
                      className="w-full"
                    />
                  </div>
                  <div className="w-full sm:w-[11.5rem] space-y-1">
                    <p className="text-[11px] font-medium text-muted-foreground">To</p>
                    <DatePartsPicker
                      value={customDraftTo}
                      onChange={(iso) => {
                        setCustomRangeError("");
                        setCustomDraftTo(iso);
                      }}
                      fromYear={2020}
                      toYear={now.getFullYear()}
                      minDate={customFromDate}
                      maxDate={now}
                      placeholder="End date"
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="premium"
                    size="sm"
                    className="h-10 px-4 sm:self-end"
                    onClick={applyCustomRange}
                    disabled={!canApplyCustomRange}
                  >
                    Apply
                  </Button>
                </div>
              ) : null}

              {customRangeError && trendRange === "custom" ? (
                <p className="text-xs text-destructive text-left lg:text-right" role="alert">
                  {customRangeError}
                </p>
              ) : null}
            </div>
          </div>

          {trendIsError ? (
            <p className="mt-4 text-sm text-destructive" role="alert">
              {trendError instanceof Error ? trendError.message : "Could not load inventory trend."}
            </p>
          ) : null}

          <div className="mt-4 h-[280px] w-full min-h-[240px]">
            {trendLoading ? (
              <div className="h-full rounded-md bg-muted/40 animate-pulse" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendChartData} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
                  <defs>
                    <linearGradient id="dashFillPrimary" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(142 70% 45% / 0.35)" />
                      <stop offset="100%" stopColor="hsl(142 70% 45% / 0)" />
                    </linearGradient>
                    <linearGradient id="dashFillSecondary" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(199 89% 48% / 0.3)" />
                      <stop offset="100%" stopColor="hsl(199 89% 48% / 0)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical horizontal className="stroke-border/60" />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    interval={xAxisInterval}
                    padding={{ left: 12, right: 12 }}
                    tickMargin={8}
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                    allowDecimals={false}
                    domain={[0, yMax]}
                    ticks={yTicks}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid hsl(var(--border))",
                      background: "hsl(var(--card))",
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload as { date?: string } | undefined;
                      if (!row?.date) return "";
                      const d = parseISO(row.date);
                      return isValid(d) ? format(d, "PPP") : row.date;
                    }}
                    formatter={(value: number, name: string) => [
                      value,
                      name === "primary" ? "Products added" : "Sales",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="primary"
                    name="primary"
                    stroke="hsl(142 55% 40%)"
                    strokeWidth={2}
                    fill="url(#dashFillPrimary)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="secondary"
                    name="secondary"
                    stroke="hsl(199 80% 48%)"
                    strokeWidth={2}
                    fill="url(#dashFillSecondary)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.section>
      </div>
    </Layout>
  );
};

export default Dashboard;
